import Device from '@ubnt/link-core/modules/devices/Device';
import Button from '@ubnt/ui-components/Button/Button';
import Input from '@ubnt/ui-components/Input/Input';
import PanelCard from '@ubnt/ui-components/PanelCard/PanelCard';
import SidePanel from '@ubnt/ui-components/SidePanel/SidePanel';
import Portal from '@ubnt/ui-components/utils/portalCreator';
import { omit } from 'lodash-es';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  ClientType,
  CustomClientFormType,
  DEFAULT_CUSTOM_CLIENT_WIFI_STANDARD,
  getFormDataBasedOnDevice,
} from '@ubnt/wifi-calculator/device/customClients';
import { findDevice } from '@ubnt/wifi-calculator/device/findDevice';
import { Flex } from 'components/Flex';
import DefaultSidePanelHeader from 'components/ui-components/DefaultSidePanelHeader';

import { DEFAULT_CUSTOM_CLIENT_NAME } from '../../constants';
import ClientTypePicker from './ClientTypePicker';
import RadioConfigs from './RadioConfigs';
import WifiStandardDropdown from './WifiStandardDropdown';

import classes from './CustomClientForm.module.scss';

export interface CustomClientFormProps {
  onClose: () => void;
  onSubmit: (formData: Required<CustomClientFormType>) => void;
  onRemoveClick?: () => void;
  open: boolean;
  usedNames?: string[];
  editDeviceName?: string;
  externalDeviceMap?: Record<string, Device>;
}

const defaultValues = {
  name: DEFAULT_CUSTOM_CLIENT_NAME,
  clientType: ClientType.Phone,
  wifiStandard: DEFAULT_CUSTOM_CLIENT_WIFI_STANDARD,
  radioParams: undefined,
};

const CustomClientForm = (props: CustomClientFormProps) => {
  const { editDeviceName, externalDeviceMap, onClose, onRemoveClick, onSubmit, open, usedNames = [] } = props;

  const form = useForm<Required<CustomClientFormType>>({
    defaultValues,
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = form;

  useEffect(() => {
    if (open && !editDeviceName) {
      reset(defaultValues);
    }
  }, [open, editDeviceName, reset]);

  useEffect(() => {
    if (editDeviceName) {
      const editDevice = findDevice({ name: editDeviceName }, externalDeviceMap);
      if (editDevice instanceof Device) {
        reset(getFormDataBasedOnDevice(editDevice, externalDeviceMap));
      }
    }
  }, [editDeviceName, externalDeviceMap, reset]);

  return (
    <Portal identifier="custom-client-form">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit(onSubmit)(e);
        }}
      >
        <SidePanel
          className={classes.slidePanel}
          header="Custom Client"
          footer={
            <Flex gap="s" justifyEnd>
              <Button type="button" onClick={onClose}>
                Cancel
              </Button>
              {editDeviceName && (
                <Button type="button" variant="secondary" color="danger" onClick={onRemoveClick}>
                  Remove
                </Button>
              )}
              <Button variant="primary">{editDeviceName ? 'Apply Changes' : 'Create'}</Button>
            </Flex>
          }
          position="fixed"
          onClose={onClose}
          renderHeader={DefaultSidePanelHeader}
          in={open}
        >
          <Flex className={classes.content} gap="l" column>
            <PanelCard>
              <Flex gap="l" column>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input label="Name" invalid={errors?.name?.message} variant="secondary" full {...field} />
                  )}
                  rules={{
                    required: 'Client name is required.',
                    validate: (value) =>
                      value && usedNames.includes(value) && value !== editDeviceName ?
                        'This name is already in use.'
                      : undefined,
                  }}
                />
                <Controller
                  name="clientType"
                  control={control}
                  render={({ field }) => <ClientTypePicker {...omit(field, 'ref')} />}
                />
              </Flex>
            </PanelCard>
            <PanelCard>
              <Controller
                name="wifiStandard"
                control={control}
                render={({ field }) => <WifiStandardDropdown {...omit(field, 'ref')} />}
              />
            </PanelCard>
            <FormProvider {...form}>
              <RadioConfigs />
            </FormProvider>
          </Flex>
        </SidePanel>
      </form>
    </Portal>
  );
};

export default CustomClientForm;
